/**
 * Runtime Cognito configuration getter (task 7.1, public-website spec).
 *
 * Per design §4.3 (Authentication — cross-stack consumption from
 * `core-infrastructure`), the SPA reads its public Cognito user pool
 * coordinates from `/config.json` at boot. The deploy step (task 11.1)
 * resolves the three CloudFormation outputs `PublicCognitoUserPoolId`,
 * `PublicCognitoUserPoolClientId`, `PublicCognitoHostedUiUrl` from the
 * deployed `core-infrastructure` follow-up stack and writes them under
 * the `cognito` slice of `config.json` as
 * `{ userPoolId, userPoolClientId, hostedUiUrl }`. The bootstrap step
 * (task 11) fetches `/config.json` and assigns the parsed object to
 * `globalThis.__DVGUP_CONFIG__`; this getter reads that same global so
 * the values flow from one canonical source — the same source backing
 * `getPublicWebsiteConfig()` in `lib/api/config.ts`.
 *
 * Until the **`core-infrastructure` follow-up** lands the new public
 * Cognito user pool + IdP attachments + app client + Hosted UI domain
 * in beta (per the Implementation Gating section of `tasks.md` and
 * design §1.1's first follow-up commit), the values are placeholder
 * strings and the SPA's federated sign-in flow (tasks 7.2–7.6) will
 * not be functional at runtime. The placeholder shape lets tasks 7.1
 * through 7.6 ship and unit-test against a stubbed config without
 * blocking on the upstream stack deploy. Tasks 7.7+ are the deploy
 * gate that asserts real values are present.
 *
 * Function-length budget: every export below stays under 20 lines per
 * Requirement 3.7.
 */

/**
 * Public-pool Cognito coordinates the SPA needs to drive the OAuth
 * Hosted UI flow (design §2.3 / Requirement 5).
 */
export interface CognitoConfig {
  /**
   * Public Cognito user pool id, sourced from
   * `core-infrastructure`'s `PublicCognitoUserPoolId` output.
   * Empty string until the follow-up lands.
   */
  readonly userPoolId: string;
  /**
   * SPA app client id (PKCE / public client), sourced from
   * `core-infrastructure`'s `PublicCognitoUserPoolClientId` output.
   * Empty string until the follow-up lands.
   */
  readonly userPoolClientId: string;
  /**
   * Origin of the Cognito Hosted UI for the public pool — the
   * OAuth `/oauth2/authorize` and `/oauth2/token` endpoints live
   * under this URL. Sourced from `core-infrastructure`'s
   * `PublicCognitoHostedUiUrl` output. Empty string until the
   * follow-up lands.
   */
  readonly hostedUiUrl: string;
}

/**
 * Read the runtime Cognito config injected at bootstrap by
 * `/config.json`'s `cognito` slice.
 *
 * Matches the convention of `getPublicWebsiteConfig()` in
 * `lib/api/config.ts`: when the global is absent (the bootstrap step
 * has not run, or the deploy step has not yet written real values)
 * return placeholder empty strings rather than throwing. The
 * federated sign-in surface (tasks 7.2–7.6) treats empty strings as
 * "Cognito not yet configured" and surfaces a non-functional sign-in
 * page; the deploy gate at task 7.7+ asserts real values are present
 * before the surface goes live.
 *
 * @returns The injected `CognitoConfig`, or
 *   `{ userPoolId: '', userPoolClientId: '', hostedUiUrl: '' }`
 *   when the bootstrap step has not yet run or the
 *   `core-infrastructure` follow-up has not yet deployed.
 */
export function getCognitoConfig(): CognitoConfig {
  const fromWindow = (
    globalThis as unknown as {
      __DVGUP_CONFIG__?: { cognito?: Partial<CognitoConfig> };
    }
  ).__DVGUP_CONFIG__;
  const slice = fromWindow?.cognito;
  return {
    userPoolId: slice?.userPoolId ?? '',
    userPoolClientId: slice?.userPoolClientId ?? '',
    hostedUiUrl: slice?.hostedUiUrl ?? '',
  };
}
